import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "api/Http";

// Actions
import { setError } from "store/slices/apiErrorsSlice";
import { setPermanentlyLocked, setTemporaryLocked } from "store/slices/applicationSlice";
import {
  setCONRegisterButtonLoader,
  setCONReminderButtonLoader,
  setCreatePasswordButtonLoader,
  setEnableTwoFactorLoader,
  setLoginButtonLoader,
  setLoginOrRegisterButtonLoader,
  setResendButtonLoader,
  setTwoFactorLoginButtonLoader,
} from "store/slices/loadersSlice";

// Helpers
import { queryParamsBuilder } from "utils/helpers";

export const callIdentify = createAsyncThunk(
  "ACCOUNT/callIdentify",
  async (_props, { dispatch }) => {
    const response = await Http.get({ url: "account/me" });

    if (response.isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (response.error) {
      dispatch(setError("callIdentify"));
    } else {
      return response;
    }
  },
);

export const checkEmail = createAsyncThunk(
  "ACCOUNT/checkEmail",
  async (_props, { dispatch, getState }) => {
    const { application } = getState();
    const email = application.email;
    const params = {};

    if (email) params.email = email;
    const queryParams = queryParamsBuilder(params);

    let response = await Http.get({
      url: `account/status?${queryParams}`,
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    error ? dispatch(setError("checkEmail")) : dispatch(setLoginOrRegisterButtonLoader(false));

    return response;
  },
  {
    condition: (_props, { getState }) => {
      const { application } = getState();
      return application.email !== "";
    },
    dispatchConditionRejection: true,
  },
);

export const getQRCode = createAsyncThunk(
  "ACCOUNT/getQRCode",
  async ({ email }, { dispatch }) => {
    const params = {};

    if (email) params.email = email;
    const queryParams = queryParamsBuilder(params);

    let response = await Http.get({
      url: `account/two-factor-qr-code?${queryParams}`,
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    error ? dispatch(setError("getQRCode")) : dispatch(setTwoFactorLoginButtonLoader(false));

    return response;
  },
  {
    dispatchConditionRejection: true,
  },
);

export const isSignedIn = createAsyncThunk("ACCOUNT/isSignedIn", async (_props, { dispatch }) => {
  const response = await Http.get({ url: "account/is-signed-in" });

  const { error, isPermanent, isTemporaryLocked } = response;

  if (isTemporaryLocked) {
    dispatch(setTemporaryLocked(true));
  }

  if (isPermanent) {
    dispatch(setPermanentlyLocked(true));
  }

  if (error) {
    dispatch(setError("isSignedIn"));
  }

  return response;
});

export const remindCON = createAsyncThunk("ACCOUNT/remindCON", async ({ email }, { dispatch }) => {
  let response = await Http.post({
    url: `account/remind-primary-code`,
    data: { email: email },
  });

  const { error, isPermanent, isTemporaryLocked } = response;

  if (isTemporaryLocked) {
    dispatch(setTemporaryLocked(true));
  }

  if (isPermanent) {
    dispatch(setPermanentlyLocked(true));
  }

  error ? dispatch(setError("remindCON")) : dispatch(setCONReminderButtonLoader(false));

  return response;
});

export const sendNewEmail = createAsyncThunk(
  "ACCOUNT/sendNewEmail",
  async ({ returnUrl }, { dispatch, getState }) => {
    const { application } = getState();
    const { email, isResetPassword } = application;

    const endPointUrl = isResetPassword ? "send-reset-email" : "resend-registration-email";

    let response = await Http.post({
      url: `account/${endPointUrl}`,
      data: { email, returnUrl: returnUrl === false ? null : returnUrl },
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    if (error) {
      dispatch(setError("sendNewEmail"));
    } else {
      dispatch(setLoginButtonLoader(false));
      dispatch(setResendButtonLoader(false));
    }
    return response;
  },
  {
    condition: (_props, { getState }) => {
      const { application } = getState();
      return application.email !== "";
    },
    dispatchConditionRejection: true,
  },
);

export const setPassword = createAsyncThunk(
  "ACCOUNT/setPassword",
  async (password, { dispatch, getState }) => {
    const { application } = getState();
    const { email, isResetPassword, token } = application;
    const endPointUrl = isResetPassword ? "reset" : "set";

    let response = await Http.post({
      url: `account/${endPointUrl}-password`,
      data: { email, password, token },
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    error ? dispatch(setError("setPassword")) : dispatch(setCreatePasswordButtonLoader(false));

    return response;
  },
  {
    condition: (_props, { getState }) => {
      const { application } = getState();
      return application.token !== "";
    },
    dispatchConditionRejection: true,
  },
);

export const signIn = createAsyncThunk(
  "ACCOUNT/signIn",
  async ({ email, password }, { dispatch }) => {
    let response = await Http.post({
      url: `account/sign-in`,
      data: { email, password },
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    if (error) {
      dispatch(setError("signIn"));
    }

    return response;
  },
);

export const signInTwoFactor = createAsyncThunk(
  "ACCOUNT/signInTwoFactor",
  async ({ email, password, verificationCode }, { dispatch }) => {
    let response = await Http.post({
      url: `account/sign-in-two-factor`,
      data: { email, password, verificationCode },
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    if (error) {
      dispatch(setError("signIn"));
    }

    return response;
  },
);

export const enableTwoFactor = createAsyncThunk(
  "ACCOUNT/enableTwoFactor",
  async ({ email, verificationCode }, { dispatch }) => {
    let response = await Http.post({
      url: `account/enable-two-factor`,
      data: {
        email: email,
        verificationCode: verificationCode,
      },
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    error ? dispatch(setError("enableTwoFactor")) : dispatch(setEnableTwoFactorLoader(false));

    return response;
  },
);

export const signUp = createAsyncThunk(
  "ACCOUNT/signUp",
  async ({ conNumber, returnUrl }, { dispatch, getState }) => {
    const { application } = getState();
    const email = application.email;

    let response = await Http.post({
      url: `account/sign-up`,
      data: { email, primaryCode: conNumber, returnUrl },
    });

    const { error, status, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    const handledStatusCodes = [200, 201, 400];
    if (handledStatusCodes.includes(status)) {
      dispatch(setCONRegisterButtonLoader(false));
    } else if (error) {
      dispatch(setError("signUp"));
    }

    return response;
  },
  {
    condition: ({ conNumber }, { getState }) => {
      const { application } = getState();
      const isNoEmail = application.email !== "";
      const isNoCONNumber = conNumber !== "";

      return isNoCONNumber && isNoEmail;
    },
    dispatchConditionRejection: true,
  },
);

export const validateToken = createAsyncThunk(
  "ACCOUNT/validateToken",
  async (_props, { dispatch, getState }) => {
    const { application } = getState();
    const { email, isResetPassword, token } = application;
    const endPointUrl = isResetPassword ? "reset-password" : "registration";
    const params = {};
    if (email) params.email = email;
    if (token) params.token = token;

    const queryParams = queryParamsBuilder(params);

    let response = await Http.get({
      url: `account/validate-${endPointUrl}-token?${queryParams}`,
    });

    const { error, isPermanent, isTemporaryLocked } = response;

    if (isTemporaryLocked) {
      dispatch(setTemporaryLocked(true));
    }

    if (isPermanent) {
      dispatch(setPermanentlyLocked(true));
    }

    if (error) {
      dispatch(setError("validateToken"));
    }

    return response;
  },
  {
    condition: (_props, { getState }) => {
      const { application } = getState();
      const { email, token } = application;
      const isNoEmail = email !== "";
      const isNoToken = token !== "";

      return isNoToken && isNoEmail;
    },
    dispatchConditionRejection: true,
  },
);

const accountSlice = createSlice({
  name: "ACCOUNT",
  initialState: {},
});

export default accountSlice.reducer;
